import { IImage, ISharImgeChat } from "redux/actions";
import styles from "./answerContainer.module.scss";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { Spinner } from "components";
import 'react-photo-view/dist/react-photo-view.css';
import { ImgDownload, ZoomIn, ZoomOut } from "../icons/ImgUserControlIcons";
import React from "react";
import classNames from "classnames";

interface IProp {
  combinedFile?:boolean;
    images: IImage[] | ISharImgeChat[];
    loading: boolean;
    handleImageLoad: () => void;
    inlineDisplay?: boolean; // New prop to indicate if displayed inline with files
    threads?:boolean;
}

export const ImagePreview = ({ images, loading, handleImageLoad, combinedFile }: IProp) => {

    if (images.length === 0) return null;

    // Function to check if an image is a GIF
    const isGif = (imagePath: string | undefined): boolean => {
      if (!imagePath) return false;
      return imagePath.toLowerCase().endsWith('.gif');
    };
    
    // Function to handle PhotoView props based on image type
    const getPhotoViewProps = (imagePath: string | undefined) => {
      // We'll return different props based on whether the image is a GIF
      if (isGif(imagePath)) {
        // For GIFs, return props that effectively disable zoom interaction
        return {
          // Default props for GIFs - doesn't allow adding custom zoom behavior
        };
      }
      
      // For regular images, return default props
      return {};
    };

    return (
        <>
            <PhotoProvider 
              maskOpacity={0.91} 
              speed={() => 800}
              easing={(type) => (type === 2 ? 'cubic-bezier(0.36, 0, 0.66, -0.56)' : 'cubic-bezier(0.34, 1.56, 0.64, 1)')}
              toolbarRender={({ onScale, scale, index }) => {
                // Check if we have a valid index and images array
                if (typeof index === 'number' && index >= 0 && index < images.length) {
                  // Get the current image being viewed based on index
                  const currentImage = images[index];
                  if (isGif(currentImage.path)) {
                    // Don't render any toolbar for GIFs
                    return null;
                  }
                }
                
                // For non-GIF images, render the toolbar normally
                return (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: 'white',
                    gap: '12px', 
                    marginTop: '12px',
                    userSelect: 'none'
                  }}>
                    {/* Zoom out button */}
                    <div onClick={() => onScale(scale - 0.5)} style={{ cursor: 'pointer', padding: '8px' }}>
                      <ZoomOut />
                    </div>
                    
                    {/* Zoom in button */}
                    <div onClick={() => onScale(scale + 0.5)} style={{ cursor: 'pointer', padding: '8px' }}>
                      <ZoomIn />
                    </div>
                    
                    {/* Download button */}
                    <div 
                      onClick={() => {
                        // Create a custom event to trigger the download
                        const event = new CustomEvent('photo-view-download-image');
                        document.dispatchEvent(event);
                      }}
                      style={{ cursor: 'pointer', padding: '8px' }}
                    >
                      <ImgDownload />
                    </div>
                  </div>
                );
              }}
            >
                {images.map((image, index) => {
                  return (
                    <React.Fragment key={index}>
                        {loading && (
                            <div>
                                {" "}
                                <Spinner extraSmall delFile/>{" "}
                            </div>
                        )}
                        <PhotoView 
                            src={image.path || ''}
                            {...getPhotoViewProps(image.path)}
                        >
                            <img
                                src={image.path || ''}
                                alt={`Image ${index + 1}`}
                                className={classNames("rounded-[10px] max-w-[84px] max-h-[84px] cursor-pointer" , {
                                  [styles.combinedFile]:combinedFile,
                                })}
                                onLoad={handleImageLoad}
                            />
                        </PhotoView>
                    </React.Fragment>
                  );
                })}
            </PhotoProvider>
        </>
    );
};